import { apiClient } from '@discovr/common';
import type { DataResponse, PaginatedResponse } from '@discovr/common';

/** Deliverable option as returned by the BE on a creator's youtube profile. */
export interface CreatorDeliverable {
  type: string;
  base_price: number;
}

/**
 * Creator as returned by GET /api/v1/creators.
 * Shape verified against live BE (2026-05-15):
 *   - youtube.is_connected  — nested boolean (NOT top-level youtube_connected)
 *   - youtube.deliverables  — array of {type, base_price} objects (NOT strings)
 */
export interface Creator {
  id: string;
  uid: string;
  email: string;
  first_name?: string;
  last_name?: string;
  /** Legacy flat name field — some rows still emit this instead of first_name/last_name. */
  name?: string;
  /**
   * Lifecycle status from `users.status` (active | shadow | pending | etc).
   * BE field is `approval_status` for legacy reasons but the value is the
   * enum, not "approved" / "rejected".
   */
  approval_status: string;
  youtube?: {
    username?: string;
    channel_name?: string;
    profile_picture?: string;
    /** Whether the creator has connected their YouTube account to Discovr. */
    is_connected?: boolean;
    deliverables?: CreatorDeliverable[];
  };
  instagram?: {
    username?: string;
    /** Whether the creator has connected their Instagram account. Always false today; OAuth deferred to v3/v4. */
    is_connected?: boolean;
  };
  created_at: string;
  /**
   * Concurrency token — admin Edit drawer echoes this back via
   * `If-Match` header so the BE can 409 on stale writes.
   */
  updated_at?: string;
  /** Contact email kept on `creator_profiles` (admin export only). */
  contact_email?: string;
  /** Phone number from `creator_profiles` (admin export only). */
  phone?: string;
  /** WhatsApp number from `creator_profiles` (admin export only). */
  whatsapp?: string;
}

/**
 * Payload for PATCH /creators/{uid}. All fields are optional; omit any
 * key to leave it unchanged. Send empty string to clear an optional
 * field (only meaningful for phone/whatsapp/contact_email/usernames).
 */
export interface UpdateCreatorBody {
  email?: string;
  first_name?: string;
  last_name?: string;
  contact_email?: string;
  phone?: string;
  whatsapp?: string;
  ig_username?: string;
  yt_username?: string;
}

/** Platform profiles that carry a country and time zone (Go #739). */
export type CreatorGeoPlatform = 'instagram' | 'youtube';

/** One platform profile's geo. Absent fields are unknown. */
export interface PlatformGeo {
  /** ISO 3166-1 alpha-2. */
  country?: string;
  country_source?: 'youtube_api' | 'phyllo' | 'admin';
  /** IANA zone; never the campaign zone. */
  timezone?: string;
  timezone_source?: 'derived' | 'phyllo' | 'admin';
}

/** GET /creators/{creator_id}/geo. A platform is null when the creator has no profile on it. */
export interface CreatorGeo {
  creator_id: string;
  instagram: PlatformGeo | null;
  youtube: PlatformGeo | null;
}

/** PATCH body: an absent field is unchanged. */
export interface UpdateCreatorGeoBody {
  country?: string;
  timezone?: string;
}

export const creators = {
  list: (params?: {
    page?: number;
    limit?: number;
    status?: string;
    sort?: string;
    order?: 'asc' | 'desc';
  }): Promise<PaginatedResponse<Creator>> =>
    apiClient.get<PaginatedResponse<Creator>>('/creators', { params }).then((r) => r.data),

  updateStatus: (uid: string, status: string): Promise<void> =>
    apiClient.post(`/creators/${encodeURIComponent(uid)}/status`, { status }).then(() => undefined),

  /**
   * Admin-only PATCH. `updatedAt` becomes the `If-Match` header so the
   * BE returns 409 when another admin has saved in the meantime.
   * Returns the refreshed Creator (with a new `updated_at`).
   */
  update: (uid: string, body: UpdateCreatorBody, updatedAt: string): Promise<Creator> =>
    apiClient
      .patch<Creator>(`/creators/${encodeURIComponent(uid)}`, body, {
        headers: { 'If-Match': updatedAt },
      })
      .then((r) => r.data),

  /** creator_id is the creator's user id (ObjectID hex). */
  getGeo: (creatorId: string): Promise<CreatorGeo> =>
    apiClient.get<DataResponse<CreatorGeo>>(`/creators/${encodeURIComponent(creatorId)}/geo`).then((r) => r.data.data),

  /** Admin override; Go keeps an admin value through later profile refreshes. 409 when the geo changed meanwhile. */
  updateGeo: (creatorId: string, platform: CreatorGeoPlatform, body: UpdateCreatorGeoBody): Promise<PlatformGeo & { platform: CreatorGeoPlatform }> =>
    apiClient
      .patch<DataResponse<PlatformGeo & { platform: CreatorGeoPlatform }>>(`/creators/${encodeURIComponent(creatorId)}/geo/${platform}`, body)
      .then((r) => r.data.data),

  getAnalyticsMetrics: (username: string, platform: 'youtube' | 'instagram'): Promise<unknown> =>
    apiClient
      .get(`/creators/${encodeURIComponent(username)}/analytics/metrics`, { params: { platform } })
      .then((r) => r.data),

  getAnalyticsSafety: (username: string, platform: 'youtube' | 'instagram'): Promise<unknown> =>
    apiClient
      .get(`/creators/${encodeURIComponent(username)}/analytics/safety`, { params: { platform } })
      .then((r) => r.data),
};
